import type { AnyModelSpec } from '@elara/firebolt/shared';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientDocumentHandle, ClientDocumentRef, ModelExtensions } from './types.js';

import { batch } from '@preact/signals-react';
import { createFlatStore } from '@preact-signals/utils';

import { getErrorReporter } from '#lib/error-reporting.js';

import { registerActiveListener } from './listener-registry.js';

const documentCache = new Map<string, ClientDocumentHandle<AnyModelSpec>>();
const RETRYABLE_SNAPSHOT_ERROR_CODES = new Set(['permission-denied', 'unauthenticated', 'unavailable']);
const MAX_SNAPSHOT_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY_MS = 300;
const MAX_RETRY_DELAY_MS = 5_000;

function normalizeFirestoreErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined;
  const code = (error as { code?: unknown }).code;
  if (typeof code !== 'string' || code.length === 0) return undefined;
  return code.includes('/') ? code.slice(code.lastIndexOf('/') + 1) : code;
}

function shouldRetrySnapshotError(error: unknown) {
  const code = normalizeFirestoreErrorCode(error);
  return code !== undefined && RETRYABLE_SNAPSHOT_ERROR_CODES.has(code);
}

/**
 * Creates a live, reactive subscription for a single Firestore document.
 *
 * Similar to `createClientQuery`, but for single documents. It provides:
 * - Real-time updates via Firestore listeners
 * - Proper data transformation through Zod schemas
 * - Integration with FirestoreCache for instance management
 * - Automatic cleanup and unsubscription
 *
 * @param db - The DI container for Firebase services.
 * @param spec - The ModelSpec for the document's collection
 * @param docRef - The Firestore document reference to subscribe to
 * @returns A reactive signal containing the document state
 *
 * @example
 * ```typescript
 * // Assuming 'services' is an initialized FirebaseServiceContainer
 * const chatHandle = createClientDocument(services, ChatSpec, chatDocRef);
 *
 * // In a component
 * if (chatHandle.isReady) {
 *   if (chatHandle.error) {
 *     // Snapshot failed (e.g. permission denied, network error)
 *     return <Fallback />;
 *   }
 *   if (chatHandle.data) {
 *     console.log('Chat title:', chatHandle.data.title);
 *   } else {
 *     console.log('Document does not exist');
 *   }
 * }
 * ```
 */
export function createClientDocument<Spec extends AnyModelSpec, Extensions extends ModelExtensions<Spec>>(
  db: FirestoreContainer,
  spec: Spec,
  docRef: ClientDocumentRef<Spec, Extensions>,
): ClientDocumentHandle<Spec, Extensions> {
  const cacheKey = `${spec.name}:${docRef.path}`;
  const cached = documentCache.get(cacheKey) as ClientDocumentHandle<Spec, Extensions> | undefined;
  if (cached) return cached;

  const [reactiveDoc, merge] = createFlatStore({
    isReady: false,
    data: undefined,
    error: undefined,
    unsubscribe: () => {},
  } as ClientDocumentHandle<Spec, Extensions>);

  let retryAttempt = 0;
  let isDisposed = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let unsubscribeFirestore: () => void = () => {};

  const clearRetryTimer = () => {
    if (retryTimer !== undefined) {
      clearTimeout(retryTimer);
      retryTimer = undefined;
    }
  };

  const subscribe = () => {
    if (isDisposed) return;
    unsubscribeFirestore();
    console.debug(
      `[createClientDocument] Subscribing to document [${spec.name}] at path: ${docRef.path} (attempt ${retryAttempt + 1}/${MAX_SNAPSHOT_RETRY_ATTEMPTS + 1})`,
    );
    unsubscribeFirestore = db.onSnapshot(
      docRef,
      (snapshot) => {
        const recoveredAttempts = retryAttempt;
        clearRetryTimer();
        if (!snapshot.metadata.fromCache) {
          retryAttempt = 0;
        }
        batch(() => {
          if (recoveredAttempts > 0 && !snapshot.metadata.fromCache) {
            console.info(
              `[createClientDocument] Snapshot recovered for [${spec.name}] at path: ${docRef.path} after ${recoveredAttempts} retry attempt(s)`,
            );
          }
          console.debug(`Received update for document [${spec.name}] at path: ${docRef.path} (fromCache: ${snapshot.metadata.fromCache})`);
          if (snapshot.exists()) {
            try {
              const instance = snapshot.data();
              merge({ isReady: true, data: instance, error: undefined });
            } catch (error) {
              const parseError = error instanceof Error ? error : new Error(String(error));
              getErrorReporter().captureException(parseError, {
                tags: { module: 'createClientDocument', spec: spec.name },
                extra: { docPath: docRef.path },
              });
              console.warn(
                `[createClientDocument] Failed to parse document [${spec.name}] at path: ${docRef.path}:`,
                parseError,
              );
              merge({ isReady: true, data: undefined, error: parseError });
            }
          } else {
            merge({ isReady: true, data: undefined, error: undefined });
          }
        });
      },
      (err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        const errorCode = normalizeFirestoreErrorCode(err);
        const retryable = shouldRetrySnapshotError(err);
        batch(() => {
          console.warn(
            `[createClientDocument] Snapshot error for [${spec.name}] at path: ${docRef.path} (code: ${errorCode ?? 'unknown'})`,
            error,
          );
          if (!retryable || retryAttempt >= MAX_SNAPSHOT_RETRY_ATTEMPTS) {
            if (!retryable) {
              console.error(
                `[createClientDocument] Not retrying snapshot for [${spec.name}] at path: ${docRef.path} because the error is non-retryable (code: ${errorCode ?? 'unknown'})`,
              );
            } else {
              console.error(
                `[createClientDocument] Exhausted snapshot retries for [${spec.name}] at path: ${docRef.path} after ${retryAttempt} retry attempt(s)`,
              );
            }
            merge({ isReady: true, error });
            return;
          }
          const delayMs = Math.min(INITIAL_RETRY_DELAY_MS * 2 ** retryAttempt, MAX_RETRY_DELAY_MS);
          retryAttempt += 1;
          console.warn(
            `[createClientDocument] Retrying snapshot for [${spec.name}] at path: ${docRef.path} in ${delayMs}ms (retry ${retryAttempt}/${MAX_SNAPSHOT_RETRY_ATTEMPTS}, code: ${errorCode ?? 'unknown'})`,
          );
          merge({ isReady: reactiveDoc.data !== undefined, error });
          clearRetryTimer();
          retryTimer = setTimeout(() => {
            retryTimer = undefined;
            subscribe();
          }, delayMs);
        });
      },
    );
  };

  subscribe();

  // Re-subscribe on app resume (ELARA-695): mobile suspends sockets on
  // background, leaving onSnapshot stalled without erroring. Resetting the
  // backoff and re-running subscribe() (which stops the prior stream first)
  // forces a fresh connection. Idempotent and a no-op once disposed.
  const unregisterResubscribe = registerActiveListener(() => {
    retryAttempt = 0;
    clearRetryTimer();
    subscribe();
  });

  reactiveDoc.unsubscribe = () => {
    isDisposed = true;
    unregisterResubscribe();
    clearRetryTimer();
    unsubscribeFirestore();
    documentCache.delete(cacheKey);
  };

  documentCache.set(cacheKey, reactiveDoc);
  return reactiveDoc;
}
